import { create } from "zustand";

import type { ValidationReport } from "@/api/hooks";

/**
 * The outcome of the last authoritative validation run, per project.
 *
 * Validation is a POST, so its result is not a query the cache can refetch —
 * but every surface needs to read it: the section diagnostics panels, the
 * lifecycle action bar, and the candidate-creation gate #8 consumes. Holding it
 * in one small store means those surfaces agree, instead of each running its
 * own validation and disagreeing about whether the draft is red.
 *
 * There is deliberately **no "assume passed"** member. An outcome the browser
 * has not seen is `not_run`, and `not_run` blocks.
 */
export type ValidationOutcome =
  /** Nothing has been run against this project in this session. */
  | { kind: "not_run" }
  | { kind: "running" }
  /**
   * The service refused a write this session believed it had certified — a
   * freeze answered `not_validated`/`stale_draft`/`stale_adoptions` — and the
   * authoritative inputs are being re-read. Explicitly a *blocking* state,
   * distinct from `not_run`: during the re-read the query cache still holds
   * the disproven data (TanStack serves invalidated entries until the refetch
   * settles), and `not_run` would hand the verdict straight back to that
   * cached durable badge — transiently re-enabling the exact action the
   * service just refused (review finding on PR #572, cycle 2).
   */
  | { kind: "resyncing" }
  /** A 201 arrived. `state` and `findings` come straight from the report. */
  | { kind: "report"; report: ValidationReport }
  /**
   * 409 — `expectedDraftVersion` was stale. The report the clinician was
   * looking at is void: the draft moved under them. Distinct from an error,
   * because the recovery is "re-run against the current version", not "retry".
   */
  | { kind: "stale"; requestId: string | undefined }
  /** 501 — the Editor Service has not built the endpoint. Terminal, no retry. */
  | { kind: "unavailable" }
  /** 403 — the caller may not run validation here. */
  | { kind: "denied" }
  /**
   * Anything else, including a network failure and a 500.
   *
   * Carries the machine-readable `code` and the `requestId` and nothing else.
   * #7's acceptance criterion is explicit that no raw stack trace and no
   * unclassified 500 message reaches the clinician, so the server's `message`
   * is deliberately dropped here rather than filtered downstream.
   */
  | { kind: "error"; code: string; requestId: string | undefined };

interface ValidationStore {
  byProject: Record<string, ValidationOutcome>;
  revisionByProject: Record<string, number>;
  set: (projectId: string, outcome: ValidationOutcome) => void;
  begin: (projectId: string) => number;
  beginResync: (projectId: string) => number;
  setIfCurrent: (
    projectId: string,
    revision: number,
    outcome: ValidationOutcome,
  ) => void;
  clear: (projectId: string) => void;
  reset: () => void;
}

export const useValidationStore = create<ValidationStore>((set, get) => ({
  byProject: {},
  revisionByProject: {},
  set: (projectId, outcome) =>
    set((state) => ({
      byProject: { ...state.byProject, [projectId]: outcome },
    })),
  begin: (projectId) => {
    const revision = (get().revisionByProject[projectId] ?? 0) + 1;
    set((state) => ({
      byProject: { ...state.byProject, [projectId]: { kind: "running" } },
      revisionByProject: {
        ...state.revisionByProject,
        [projectId]: revision,
      },
    }));
    return revision;
  },
  /**
   * Enters the blocking `resyncing` state and returns its revision, so the
   * settle handler can hand the verdict back (`setIfCurrent` with `not_run`)
   * without clobbering a validation run the clinician started meanwhile —
   * the same revision discipline `begin`/`setIfCurrent` already carry.
   */
  beginResync: (projectId) => {
    const revision = (get().revisionByProject[projectId] ?? 0) + 1;
    set((state) => ({
      byProject: { ...state.byProject, [projectId]: { kind: "resyncing" } },
      revisionByProject: {
        ...state.revisionByProject,
        [projectId]: revision,
      },
    }));
    return revision;
  },
  setIfCurrent: (projectId, revision, outcome) =>
    set((state) =>
      state.revisionByProject[projectId] === revision
        ? { byProject: { ...state.byProject, [projectId]: outcome } }
        : state,
    ),
  clear: (projectId) =>
    set((state) => {
      const { [projectId]: _removed, ...rest } = state.byProject;
      return {
        byProject: rest,
        revisionByProject: {
          ...state.revisionByProject,
          [projectId]: (state.revisionByProject[projectId] ?? 0) + 1,
        },
      };
    }),
  reset: () => set({ byProject: {}, revisionByProject: {} }),
}));

const NOT_RUN: ValidationOutcome = { kind: "not_run" };

export function useValidationOutcome(projectId: string): ValidationOutcome {
  return useValidationStore((state) => state.byProject[projectId] ?? NOT_RUN);
}
